// interceptor for handling auth failures on responses: 401 (logout), 402, 403
import { Injectable } from '@angular/core';
import {
	HttpRequest,
	HttpHandler,
	HttpEvent,
	HttpInterceptor,
	HttpResponse,
	HttpErrorResponse
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { NotificationService } from '../_services/notification.service';
import { TranslationService } from '../_services/translation.service';
import { ToasterTypeEnum } from '../_enums/toaster-type.enum';
import { SeverityEnum } from '../_enums/severity.enum';
import { GuestConversionService } from '../_services/guest-conversion.service';
import { UserService } from '../_services/user.service';

// A 401 from the auth middleware carries a bare {error: "..."} - the token is expired,
// revoked, or its owner was blocked mid-session. A 401 raised by a route instead carries
// the {success, data} envelope (e.g. a wrong password on login), which must not log anyone
// out. The two shapes are what tells them apart.
const isDeadTokenResponse = (err: HttpErrorResponse): boolean =>
	err.status === 401 && typeof err.error?.error === 'string';

@Injectable({
	providedIn: 'root'
})
export class AuthorizationInterceptor implements HttpInterceptor {
	constructor(
		public notificationService: NotificationService,
		public t: TranslationService,
		private guestConversionService: GuestConversionService,
		private userService: UserService,
	) {}

	intercept(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {

		request = request.clone({
			setHeaders: {
				'Cache-Control': 'No-Cache',
				'Pragma': 'No-Cache'
			},
		});

		return next.handle(request).pipe(
			tap(
				(event: HttpEvent<any>) => {
					if (event instanceof HttpResponse) {
						// do stuff with response if you want
					}
				},
				(err: any) => {
					if (err instanceof HttpErrorResponse) {
						if (isDeadTokenResponse(err)) {
							this.forceLogout();
						}

						if (err.status === 402) {
							this.guestConversionService.showConversionModal();
						}

						if (err.status === 403) {
							const doNotShowToasterForTheseUrls = [
								'/application-languages/',
								'/email/verify',
								'/user/ssologin'
							];
							let showToaster = true;
							doNotShowToasterForTheseUrls.forEach(url => {
								if (err.url.includes(url)) {
									showToaster = false;
								}
							});
							if (showToaster) {
								this.notificationService.showNotification(
									ToasterTypeEnum.DEFAULT,
									SeverityEnum.ERROR,
									{ text: this.t.t.permissionForbiddenMessage },
									5000
								);
							}
						}
					}
				}
			)
		);
	}

	private forceLogout(): void {
		// The sign out clears the session synchronously, so this also stops the other
		// requests that failed alongside this one from each triggering their own.
		if (!this.userService.isLoggedIn()) {
			return;
		}

		// The token is already dead, so the logout API calls would only 401 back into here.
		this.userService.logoutApisCalled = true;

		this.userService.triggerSignOutSubject.next(true);
	}
}
